import React, { FunctionComponent, useCallback } from 'react';
import { Popconfirm, Table, Tag } from 'antd';
import { useStore } from 'effector-react';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import moment from 'moment';

import blockedEmailDomains, {
  BlockedEmailDomainsSort,
  getList,
  updateStatus,
} from '@stores/blockedEmailDomains';
import IBlockedEmailDomain from '@interfaces/IBlockedEmailDomain';
import { pageSizeOptions } from '@constants/pagination';
import { FULL_TIME_FORMAT } from '@config/format';
import useTranslate from '@hooks/useTranslate';
import componentContent from './BlockedEmailDomainsTable.content.json';

const BlockedEmailDomainsTable: FunctionComponent = () => {
  const { list, pagination, preloaders, filters } = useStore(blockedEmailDomains);
  const content = useTranslate(componentContent);

  const onTableChange = useCallback(
    (nextPagination: TablePaginationConfig, _: any, sorter: any) => {
      localStorage.setItem(
        'blockedEmailDomainsPageSize',
        `${nextPagination.pageSize || pagination.pageSize || 10}`,
      );

      let sort: BlockedEmailDomainsSort | undefined;
      const field = sorter.field || sorter.columnKey;
      if (sorter.order && field === 'status') {
        sort = sorter.order === 'ascend' ? 'STATUS_ASC' : 'STATUS_DESC';
      }
      if (sorter.order && field === 'updatedAt') {
        sort = sorter.order === 'ascend' ? 'UPDATED_AT_ASC' : 'UPDATED_AT_DESC';
      }

      getList({ pagination: nextPagination, sort });
    },
    [pagination.pageSize],
  );

  const columns: ColumnsType<IBlockedEmailDomain> = [
    {
      title: content.domain,
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: content.responsible,
      dataIndex: 'admin',
      key: 'admin',
      render: (admin) => admin?.nickname || admin?.name || admin?.email || '—',
    },
    {
      title: content.updatedAt,
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder:
        filters.sort === 'UPDATED_AT_ASC'
          ? 'ascend'
          : filters.sort === 'UPDATED_AT_DESC'
          ? 'descend'
          : undefined,
      render: (updatedAt?: string | null) =>
        updatedAt ? moment(updatedAt).format(FULL_TIME_FORMAT) : '—',
    },
    {
      title: content.status,
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder:
        filters.sort === 'STATUS_ASC'
          ? 'ascend'
          : filters.sort === 'STATUS_DESC'
          ? 'descend'
          : undefined,
      render: (status, item) => {
        const enabled = item.status === 'ENABLED';
        const isLoading = preloaders.updateBlockedEmailDomainStatus === item.domain;

        return (
          <Popconfirm
            placement='left'
            title={enabled ? content.disableConfirm : content.enableConfirm}
            onConfirm={() =>
              updateStatus({
                domain: item.domain,
                status: enabled ? 'DISABLED' : 'ENABLED',
                preloaderId: item.domain,
              })
            }
            disabled={isLoading}
          >
            <Tag
              color={status === 'ENABLED' ? 'success' : 'default'}
              style={{
                cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {status === 'ENABLED' ? content.enabled : content.disabled}
            </Tag>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Table
      rowKey='domain'
      dataSource={list}
      columns={columns}
      loading={!!preloaders.getBlockedEmailDomains}
      onChange={onTableChange}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions,
        position: ['topRight', 'bottomRight'],
      }}
    />
  );
};

export default BlockedEmailDomainsTable;
